/*
 * PRD-037 — "Configurar comercio": el partner pre-llena TODA la configuracion del comercio sobre el
 * borrador para que el onboarding del dueno sea de <1 min (solo confirmar).
 *
 * Estrategia de reuso (como el smart menu): se abre una sesion-draft (useDraftSession deja el
 * JWT-draft en localStorage como token de comercio) y se reusan los componentes prop-driven del
 * panel — GeneralInfo, BusinessTypeV4, ScheduleManager, WizardV4 + los validators puros del wizard —
 * alimentados por un estado de comercio SIN Redux (useDraftCommerce). Las dos secciones muy
 * acopladas del panel (DeliveryPaymentFormV2 y AddressForm dependen de Redux/contexts/@plick-ui+
 * Tailwind) se replican en V4 (ConfigureDeliverySection/ConfigureAddressSection) reusando el mismo
 * modelo de datos y validadores. Los 5 grupos persisten por el mismo PUT /v1/comercio.
 *
 * NO se setea `onboarding_finish_date`: eso lo sigue escribiendo el wizard del panel cuando el dueno
 * pulsa "Finalizar" (se respeta el PRD).
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ThemeProvider } from '@plick/ui';
import { plickTheme } from '@theme/index';
import { WizardV4, type WizardV4Step } from '@components/v4/Wizard';
import GeneralInfo from '@pages/OnBoarding/steps/GeneralInfo';
import BusinessTypeV4 from '@pages/OnBoarding/steps/BusinessTypeV4';
import ScheduleManager from '@components/schedule/ScheduleManager';
import { validateBusinessTypeValues, validateCommerce } from '@pages/OnBoarding/utils/validators';
import { usePartnerAuth } from '../PartnerAuthContext';
import { useDraftSession } from '../useDraftSession';
import { useDraftCommerce } from '../useDraftCommerce';
import { ConfigureAddressSection } from '../ConfigureAddressSection';
import { ConfigureDeliverySection } from '../ConfigureDeliverySection';
import { Button, Card, ErrorNote, Muted, PageShell, Title } from '../ui';

// Carga el borrador y monta el wizard. Se renderiza SOLO cuando la sesion-draft ya esta lista
// (token en localStorage), asi el GET/PUT /v1/comercio quedan scoped al borrador.
const ConfigureInner = ({ idComercio }: { idComercio: string }) => {
  const navigate = useNavigate();
  const { commerce, loading, saving, loadError, handleUpdateValue, save } = useDraftCommerce();
  const [saveError, setSaveError] = useState<string | null>(null);

  const backToDetail = () => navigate(`/partner/comercios/${idComercio}`);

  // Persiste el borrador. En error fija el mensaje y RE-LANZA para que el wizard no avance de paso
  // (mismo contrato que handleSubmitChanges del panel).
  const persist = async () => {
    try {
      await save();
      setSaveError(null);
    } catch (err) {
      setSaveError('No se pudo guardar la configuracion. Revisa los datos e intenta de nuevo.');
      throw err;
    }
  };

  if (loadError) {
    return (
      <Card>
        <Title>No se pudo abrir el borrador</Title>
        <Muted>Intenta de nuevo o vuelve al detalle del comercio.</Muted>
        <Button variant="secondary" onClick={backToDetail} style={{ marginTop: 14 }}>
          Volver al comercio
        </Button>
      </Card>
    );
  }

  if (loading || !commerce) {
    return (
      <Card>
        <Title>Cargando configuracion...</Title>
        <Muted>Abriendo el borrador del comercio.</Muted>
      </Card>
    );
  }

  const generalMissing = validateCommerce(commerce);
  const businessMissing = validateBusinessTypeValues(commerce);

  const steps: WizardV4Step[] = [
    {
      component: (
        <GeneralInfo commerce={commerce} missingValues={generalMissing} handleUpdateValue={handleUpdateValue} />
      ),
      onSubmit: persist,
      disableNextButton: generalMissing.length > 0 || saving,
    },
    {
      component: <BusinessTypeV4 commerce={commerce} handleUpdateValue={handleUpdateValue} />,
      onSubmit: persist,
      disableNextButton: businessMissing.length > 0 || saving,
    },
    {
      component: <ConfigureAddressSection commerce={commerce} handleUpdateValue={handleUpdateValue} />,
      onSubmit: persist,
      disableNextButton: saving,
    },
    {
      component: <ConfigureDeliverySection commerce={commerce} handleUpdateValue={handleUpdateValue} />,
      onSubmit: persist,
      disableNextButton: saving,
    },
    {
      component: (
        <ScheduleManager
          autoSave
          schedules={commerce.horarios || []}
          onChange={(s) => handleUpdateValue('horarios', s)}
          customTitle="Horarios de atencion:"
        />
      ),
      onSubmit: persist,
      disableNextButton: saving,
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 14 }}>
        <Button variant="ghost" onClick={backToDetail}>
          ← Volver al comercio
        </Button>
      </div>
      <WizardV4
        title="Configurar comercio"
        subtitle="Pre-llena la configuracion del negocio. Entre mas completes, mas rapido sera el alta del dueno (solo confirmar)."
        isLoading={saving}
        steps={steps}
        onFinish={async () => {
          await persist();
          backToDetail();
        }}
      />
      {saveError && <ErrorNote>{saveError}</ErrorNote>}
    </>
  );
};

const ConfigureCommerce = () => {
  const { id } = useParams<{ id: string }>();
  const { token } = usePartnerAuth();
  const navigate = useNavigate();
  const { ready, error } = useDraftSession(token, id);

  if (error) {
    return (
      <PageShell>
        <Card>
          <Title>Configurar comercio</Title>
          <ErrorNote>{error}</ErrorNote>
          <Button variant="secondary" onClick={() => navigate('/partner')} style={{ marginTop: 14 }}>
            Volver al portal
          </Button>
        </Card>
      </PageShell>
    );
  }

  if (!ready || !id) {
    return (
      <PageShell>
        <Card>
          <Title>Preparando configuracion...</Title>
          <Muted>Abriendo la sesion del borrador.</Muted>
        </Card>
      </PageShell>
    );
  }

  // ThemeProvider: GeneralInfo (@plick/ui Tooltip) y ScheduleManager (@plick/ui Button/Switch/Input)
  // leen el tema; la app del partner no lo monta. Es solo contexto de tema, sin Redux.
  return (
    <ThemeProvider theme={plickTheme}>
      <PageShell>
        <ConfigureInner idComercio={id} />
      </PageShell>
    </ThemeProvider>
  );
};

export default ConfigureCommerce;
